import _ from 'lodash'
import { MutationTree } from 'vuex'
import { ReservationsState } from '.'
import Pagination, {PaginationInput} from '@/modules/core/model/pagination'
import Reservation from '../model/Reservation'
import { setPagination } from '@/modules/core/vuex/mutation'
import Department from '../model/Department'
import Event from '../model/Event'
import DepartmentType from '../model/DepartmenType'
import Service from '@/modules/reservation/model/Service'
import {ReservationPaginationFilters} from '@/modules/reservation/api'
import User from '@/modules/user/model/User';
import BulkProcess from '@/modules/reservation/model/BulkProcess';
import {BulkOperation} from '@/modules/reservation/model/BulkOperation';
import PrintResult from "@/modules/reservation/model/PrintResult";

export enum MutationTypes {
    SetReservationPagination = 'SET_RESERVATION_PAGINATION',
    SetDepartmentPagination = 'SET_DEPARTMENT_PAGINATION',
    ToggleDepartmentDetail = 'TOGGLE_DEPARTMENT_DETAIL',
    SetCurrentDepartment = 'SET_CURRENT_DEPARTMENT',
    UpdateCurrentDepartmentField = 'UPDATE_CURRENT_DEPARTMENT_FIELD',
    SetDepartmentTypes = 'SET_DEPARTMENT_TYPES',
    SetCurrentDerpartmentType = 'SET_CURRENT_DEPARTMENT_TYPE',
    RemoveDepartmentType = 'REMOVE_DEPARTMENT_TYPE',
    SetCurrentDepartmentField = 'SET_CURRENT_DEPARMENT_FIELD',
    UpdateReservationField = 'UPDATE_RESERVATION_FIELD',
    SetCurrentReservation = 'SET_CURRENT_RESERVATION',
    ToggleDepartmentTypeDetail = 'TOGGLE_DEPARTMENT_TYPE_DETAIL',
    UpdateCurrentDepartmentTypeField = 'UPDATE_CURRENT_DEPARTMENT_TYPE_FIELD',
    SetServicesList = 'SET_SERVICES_LIST',
    SetCurrentService = 'SET_CURRENT_SERVICE',
    ToggleServiceDetail = 'TOGGLE_SERVICE_DETAIL',
    UpdateCurrentServiceField = 'UPDATE_CURRENT_SERVICE_FIELD',
    // Mutaciones para eventos
    SetEventList = 'SET_EVENT_LIST',
    SetCurrentEvent = 'SET_CURRENT_EVENT',
    AddEvent = 'ADD_EVENT',
    RemoveEvent = 'REMOVE_EVENT',
    UpdateCurrentEventField = 'UPDATE_CURRENT_EVENT_FIELD',
    ToggleLoadingEventList = 'TOGGLE_LOADING_EVENT_LIST',

    SetReservationPaginationInput = 'SET_RESERVATION_PAGINATION_INPUT',
    ToggleLoadingReservationPagination = 'TOGGLE_LOADING_RESERVATION_PAGINATION',
    ClearReservationPaginationInputFilters = 'CLEAR_RESERVATION_PAGINATION_INPUT_FILTERS',
    SetReservationDatesAvailability = 'SET_RESERVATION_DATES_AVAILABILITY',
    SetEstimatedReservationPrice = 'SET_ESTIMATED_RESERVATION_PRICE',
    SetBulkReservationList = 'SET_BULK_RESERVATION_LIST',
    UpdateBulkOption = 'UPDATE_BULK_OPTION',
    ToggleGeneratingBulkList = 'TOGGLE_GENERATING_BULK_LIST',
    ResetBulkOptions = 'RESET_BULK_OPTIONS',
    ResetBulkProcess = 'RESET_BULK_PROCESS',
    UpdateBulkProcess = 'UPDATE_BULK_PROCESS',

    // Mutaciones para la impresión de reservas
    ToggleDisplayPrintReservation = 'TOGGLE_DISPLAY_PRINT_RESERVATION',
    SetCurrentPrintResult = 'SET_CURRENT_PRINT_RESULT',
    ToggleLoadingPrint = 'TOGGLE_LOADING_PRINT',
}

const mutations: MutationTree<ReservationsState> = {
    // Inserta una paginación de reservas en el estado
    [MutationTypes.SetReservationPagination]: setPagination('reservationPagination'),
    [MutationTypes.SetDepartmentPagination]: setPagination('departmentPagination'),
    [MutationTypes.ToggleDepartmentDetail]: (state, toggle: boolean) => state.departmentDetail = toggle,
    [MutationTypes.SetCurrentDepartment]: (state, department: Department) => state.currentDepartment = department,
    [MutationTypes.UpdateCurrentDepartmentField]: (state, {field, value}) => {
        _.set(state.currentDepartment, field, value)
    },
    [MutationTypes.SetDepartmentTypes]: (state, types: DepartmentType[]) => state.departmentTypes = types,
    [MutationTypes.SetCurrentDerpartmentType]: (state, departmentType: DepartmentType) => {
        state.currentDepartmentType = departmentType
    },

    /**
     * Elimina un tipo de departamento de la lista actual
     *
     * @param state
     * @param id
     */
    [MutationTypes.RemoveDepartmentType]: (state, id: number) => {
        const types = state.departmentTypes
        const index = types.findIndex(type => type.id === id)

        index !== -1 && types.splice(index, 1)
    },

    /**
     * Establece el valor para un campo del departamento actualmente editado
     * por el usuario
     */
    [MutationTypes.SetCurrentDepartmentField]: (state, payload: { field: string, value: any }) => {
        _.set(state.currentDepartment, payload.field, payload.value)
    },

    [MutationTypes.UpdateReservationField]: (state, payload: { field: string, value: any }) => {
        _.set(state.currentReservation, payload.field, payload.value)
    },

    /**
     * Establece la reserva actual que será mostrada al usuario
     *
     * @param state
     * @param reservation
     */
    [MutationTypes.SetCurrentReservation]: (state, reservation: Reservation) => {
        state.currentReservation = reservation
    },

    /**
     * Cambia la visibilidad del componente que muestra la información del tipo de departamento
     *
     * @param state
     * @param show
     */
    [MutationTypes.ToggleDepartmentTypeDetail]: (state, show: boolean) => {
        state.departmentTypeDetail = show
    },
    /**
     * Actualiza un campo del tipo de departamento actualmente seleccionado por el usuario
     *
     * @param state
     * @param payload
     */
    [MutationTypes.UpdateCurrentDepartmentTypeField]: (state, payload: { field: string, value: any }) => {
        _.set(state.currentDepartmentType, payload.field, payload.value)
    },

    /**
     * Establece la lista de servicios registrados
     *
     * @param state
     * @param services
     */
    [MutationTypes.SetServicesList]: (state, services: Service[]) => state.services = services,

    /**
     * Establece el servicio actualmente en edición/visualización para el usuario
     *
     * @param state
     * @param service
     */
    [MutationTypes.SetCurrentService]: (state, service: Service) => state.currentService = service,

    /**
     * Active o desactiva el componente para la edición/visualización de
     * un servicio de reserva
     */
    [MutationTypes.ToggleServiceDetail]: (state, active: boolean) => state.serviceDetail = active,

    /**
     * Actualiza un campo del tipo de servicio de reserva actualmente seleccionado
     * por el usuario
     *
     * @param state
     * @param payload
     */
    [MutationTypes.UpdateCurrentServiceField]: (state, payload: { field: string, value: any }) => {
        _.set(state.currentService, payload.field, payload.value)
    },
    [MutationTypes.SetEventList]: (state, events: Event[]) => state.events = events,
    [MutationTypes.SetCurrentEvent]: (state, event: Event) => state.currentEvent = event,
    [MutationTypes.UpdateCurrentEventField]: (state, payload: { field: string, value: any }) => {
        _.set(state.currentEvent, payload.field, payload.value)
    },
    [MutationTypes.ToggleLoadingEventList]: (state, loading: boolean) => state.loadingEventList = loading,

    /**
     * Añade un evento a la lista de evento ya cargada, si el evento ya existe en la lista
     * será sustituido por el nuevo valor.
     * @param state
     * @param event
     */
    [MutationTypes.AddEvent]: (state, event: Event) => {
        const index = state.events.findIndex(e => e.id === event.id)
        index === -1 ? state.events.push(event) : state.events.splice(index, 1, event)
    },

    /**
     * Elimina un evento de la lista de eventos cargada actualmente en la aplicación
     *
     * @param state
     * @param event
     */
    [MutationTypes.RemoveEvent]: (state, event: number) => {
        const index = state.events.findIndex(e => e.id === event)
        index > -1 && state.events.splice(index, 1)
    },

    /**
     * Establece los parámetros actuales para la paginación de reservas
     *
     * @param state
     * @param input
     */
    [MutationTypes.SetReservationPaginationInput]: (state, input: PaginationInput<ReservationPaginationFilters>) => {
        state.reservationPaginationInput = input
    },

    /**
     * Establece si se esta cargando o no una paginación de reservas
     *
     * @param state
     * @param loading
     */
    [MutationTypes.ToggleLoadingReservationPagination]: (state, loading: boolean) => {
        state.loadingReservationPagination = loading
    },

    /**
     * Restablece los filtros de la paginación de reservas a su valor original
     * @param state
     */
    [MutationTypes.ClearReservationPaginationInputFilters]: (state) => {
        const filters = state.reservationPaginationInput.filters!
        filters.username = ''
        filters.userId = 0
        filters.department = 0
        filters.date = null
        filters.startDate = null
        filters.endDate = null
        filters.reservationId = 0
    },

    /**
     * Guarda en el estado una lista de disponibilidad de fechas
     *
     * @param state
     * @param dates
     */
    [MutationTypes.SetReservationDatesAvailability]: (state, dates: Array<{ [key: string]: boolean }>) => {
        state.reservationDatesAvailability = dates
    },

    /**
     * Establece el precio estimado de la reserva en edición por el usuario
     *
     * @param state
     * @param price
     */
    [MutationTypes.SetEstimatedReservationPrice]: (state, price: number) => state.estimatedReservationPrice = price,

    /**
     * Establece la paginación de operaciones de inserción mostrando actualmente al usuario
     *
     * @param state
     * @param list
     */
    [MutationTypes.SetBulkReservationList]: (state, list: Pagination<BulkOperation>) => {
        state.bulkReservationList = list
    },

    /**
     * Actualiza un campo de las opciones para la creación masiva de reservas
     *
     * @param state
     * @param payload
     */
    [MutationTypes.UpdateBulkOption]: (state, payload: { field: string, value: any }) => {
        _.set(state.bulkOptions, payload.field, payload.value)
    },

    [MutationTypes.ToggleGeneratingBulkList]: (state, generating: boolean) => {
        state.generatingBulkList = generating
    },

    [MutationTypes.ResetBulkOptions]: (state) => {
        Object.assign(state.bulkOptions, {
            departmentType: new DepartmentType(),
            hours: [],
            departments: [],
            dateStart: '',
            dateEnd: '',
            user: new User(),
            event: new Event(),
        })
    },

    /**
     * Resetea el estado del proceso masivo de reservas
     *
     * @param state
     */
    [MutationTypes.ResetBulkProcess]: (state) => state.bulkProcess = new BulkProcess(),

    [MutationTypes.UpdateBulkProcess]: (state, payload: {field: string, value: any}) => {
        _.set(state.bulkProcess, payload.field, payload.value)
    },

    /**
     * Controla la visibilidad de la pantalla que muestra una factura en formato de impresión
     *
     * @param state
     * @param display
     */
    [MutationTypes.ToggleDisplayPrintReservation]: (state, display: boolean) => {
        state.displayPrintReservation = display
    },

    /**
     * Guarda en el estado el resultado de una impresión
     *
     * @param state
     * @param result
     */
    [MutationTypes.SetCurrentPrintResult]: (state, result: PrintResult) => {
        state.printResult = result
    },
    /**
     * Indica si la aplicación esta generado la impresión de una reserva
     *
     * @param state
     * @param loading
     */
    [MutationTypes.ToggleLoadingPrint]: (state, loading: boolean) => {
        state.loadingPrint = loading
    },
}


export default mutations
